import { instance as getApi } from '@/helpers/api';
import { getI18nInstance } from '@/i18n';
import { hex_sha1 } from '@appvise/digest-sha1';
import { userInfo } from '@/helpers/globals';
import { ROLES } from '@/models/User';
import { IUser } from '@/@types/IUser';

/** only used when localStorage is not available */
let noStorageCsrfToken: string | null = null;

/**
 * Stores the CSRF token in the browser's localStorage
 * for further use during the session.
 *
 * This is needed to support multiple browser windows.
 */
export function storeCsrfToken(token: string) {
    if (Storage) {
        localStorage.setItem('minasa.csrfToken', token);
    } else {
        noStorageCsrfToken = token;
    }
}

/**
 * Retrieves the csrf token from the browser's
 * local storage
 */
export function getCsrfToken(): string {
    if (Storage) {
        return localStorage.getItem('minasa.csrfToken') || '';
    } else {
        return noStorageCsrfToken || '';
    }
}

export async function login(
    username: string,
    password: string,
): Promise<{ user?: IUser; redirectUrl?: string; password_change?: boolean }> {
    const vueI18n = getI18nInstance();
    try {
        const response = await getApi().post('/LoginLogout/login', {
            username: username,
            password: hex_sha1(password),
        });
        if (response.data.user) {
            let redirectUrl = '/';
            const split = window.location.href.split('?redirect=');
            if (split.length === 2) {
                redirectUrl = split[1];
            }
            return {
                user: response.data.user,
                redirectUrl: redirectUrl,
                password_change: response.data.change_password,
            };
        } else if (response.data.change_password) {
            return {
                password_change: response.data.change_password,
            };
        } else {
            throw new Error(vueI18n.t('denied_wrong_login') as string);
        }
    } catch (e) {
        // if (e.response && e.response.data && e.response.data.error && e.response.data.error.msg) {
        //     throw new Error(vueI18n.t(e.response.data.error.msg));
        // }
        throw new Error(vueI18n.t('denied_wrong_login') as string);
    }
}

export function userIs(role: string | Array<string>): boolean {
    const user = userInfo();
    if ('string' === typeof role) {
        role = [role];
    }
    if (role instanceof Array) {
        for (let j = 0; j < role.length; j++) {
            const r = (role[j] || '').toLowerCase();
            const userRole = (user.role || '').toLowerCase();
            if (r === userRole) {
                return true;
            }
        }
    }

    return false;
}

export function userIsAdmin(): boolean {
    return userIs([ROLES.ADMIN]);
}

export function userIsStaff(): boolean {
    return userIs([ROLES.ADMIN, ROLES.EDITOR, ROLES.WRITER, ROLES.MANAGER]);
}

export function userIsOrganizer(): boolean {
    return userIs(ROLES.EVENT_ORGANIZER);
}

export function userIsSingleOrganizer() {
    return userIsOrganizer() && userInfo().organizer_group_id <= 0;
}

export async function logout(): Promise<void> {
    await getApi().get('/LoginLogout/logout');
}

export default {
    login,
    logout,
    // resetPassword,
    // verifyUser
};
